/** Shared workspace members cache — avoids redundant fetches across components. */

export interface MemberInfo {
  name?: string;
  avatarUrl?: string;
}

let cache: Map<string, MemberInfo> | null = null;
let inflight: Promise<Map<string, MemberInfo>> | null = null;
let lastToken: string | null = null;

/**
 * Get workspace members map. Returns cached data immediately if available,
 * otherwise fetches once and shares the result across all callers.
 */
export function getMembers(token: string): { data: Map<string, MemberInfo> | null; promise: Promise<Map<string, MemberInfo>> } {
  // If token changed (re-login), invalidate cache
  if (token !== lastToken) {
    cache = null;
    inflight = null;
    lastToken = token;
  }

  if (cache) return { data: cache, promise: Promise.resolve(cache) };

  if (!inflight) {
    inflight = fetch('/auth/workspace/members', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        const map = new Map<string, MemberInfo>();
        if (data?.members) {
          for (const m of data.members) {
            map.set(m.userId, { name: m.name, avatarUrl: m.avatarUrl });
          }
        }
        cache = map;
        inflight = null;
        return map;
      })
      .catch(() => {
        inflight = null;
        return new Map<string, MemberInfo>();
      });
  }

  return { data: null, promise: inflight };
}

/** Invalidate cache (e.g. after name/avatar change). */
export function invalidateMembersCache() {
  cache = null;
  inflight = null;
}
